import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Gauge, Shield, Zap, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { services, certifications, testimonials } from '../mock';

const Home = () => {
  return (
    <div className="bg-gray-950 text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1713371398485-7bde1bde9def?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxDTkMlMjBtYWNoaW5pbmd8ZW58MHx8fHwxNzY4MzU1MjQ2fDA&ixlib=rb-4.1.0&q=85"
            alt="CNC Machining"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/95 to-gray-950/80"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">We Deliver </span>
              <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                When Failure Is Not An Option.
              </span>
            </h1>
            <p className="!text-xl mb-8 text-gray-300">Precision manufacturing for mission-critical aerospace and defense programs.
We commit only to what we can deliver — and we deliver what we commit.

No overpromising. No surprises. Just reliable execution.</p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/contact">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-md font-semibold transition-all duration-300 shadow-lg hover:shadow-red-600/50 flex items-center space-x-2">
                  <span>Request a Quote</span>
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-6 text-lg rounded-md font-semibold transition-all duration-300">
                  View Services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-gray-500 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-red-500 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-900 py-16 border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="md:text-5xl !font-bold !text-4xl mb-2 text-red-500">±0.0002"</div>
              <div className="text-gray-400">Tight Tolerances</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">AS9100</div>
              <div className="text-gray-400">Rev. D Certified</div>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">100%</div>
              <div className="text-gray-400">Quality Focused</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Aether Machining</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Precision manufacturing backed by aerospace-grade quality systems
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-gray-900 border-gray-800 p-6 hover:border-red-600 transition-all duration-300 hover:shadow-lg hover:shadow-red-600/20">
              <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Gauge className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Tight Tolerances</h3>
              <p className="text-gray-400">Precision machining with tolerances as tight as ±0.0002" for critical aerospace components.

              </p>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6 hover:border-red-600 transition-all duration-300 hover:shadow-lg hover:shadow-red-600/20">
              <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">AS9100 Certified</h3>
              <p className="text-gray-400">
                Full AS9100 Rev. D compliance with documented quality management systems and traceability.
              </p>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6 hover:border-red-600 transition-all duration-300 hover:shadow-lg hover:shadow-red-600/20">
              <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Fast Turnaround</h3>
              <p className="text-gray-400">
                Rapid prototyping and production machining with reliable delivery schedules you can count on.
              </p>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6 hover:border-red-600 transition-all duration-300 hover:shadow-lg hover:shadow-red-600/20">
              <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Expert Team</h3>
              <p className="text-gray-400">
                Experienced machinists and engineers dedicated to delivering exceptional results every time.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive CNC machining solutions for aerospace and industrial applications
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) =>
            <Card key={service.id} className="bg-gray-950 border-gray-800 overflow-hidden group hover:border-red-600 transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  style={service.id === 'production-machining' ? { objectPosition: 'center -3px' } : {}} />

                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-400 mb-4">{service.shortDesc}</p>
                  <Link to={`/services/${service.id}`}>
                    <Button variant="ghost" className="text-red-500 hover:text-red-400 p-0 h-auto font-semibold group">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </Card>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-semibold transition-all duration-300">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-20 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://images.pexels.com/photos/5953727/pexels-photo-5953727.jpeg"
            alt="Quality Background"
            className="w-full h-full object-cover" />

        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Quality & Certifications</h2>
            <p className="max-w-3xl !text-xl mx-auto text-gray-400">Our AS9100 Rev. D quality system ensures consistent precision, repeatability, and reliability across every machined component we deliver.


            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) =>
            <Card key={index} className="bg-gray-900/80 backdrop-blur-sm border-gray-800 p-6 text-center">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">{cert.name}</h3>
                <p className="text-gray-400">{cert.description}</p>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Trusted by aerospace, defense, and industrial leaders
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="bg-gray-950 border-gray-800 p-6">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) =>
                <svg key={i} className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                )}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-sm text-gray-400">{testimonial.role}</p>
                  <p className="text-sm text-gray-500">{testimonial.company}</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-gray-900/20 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact us today to discuss your precision machining needs. 
            Our team is ready to deliver aerospace-grade quality for your next project.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/contact">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-semibold transition-all duration-300 shadow-lg hover:shadow-red-600/50">
                Request a Quote
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-3 rounded-md font-semibold">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;